import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
    Shield, 
    Award, 
    Users, 
    Globe,
    ArrowRight,
    CheckCircle2,
    Building2
} from 'lucide-react';

export const AboutSection = ({ onContactClick }) => {
    const { t } = useTranslation();

    const features = [
        {
            icon: Shield,
            title: t('about.features.security'),
            description: t('about.features.securityDesc'),
        },
        {
            icon: Award,
            title: t('about.features.experience'),
            description: t('about.features.experienceDesc'),
        },
        {
            icon: Users,
            title: t('about.features.personalized'),
            description: t('about.features.personalizedDesc'),
        },
        {
            icon: Globe,
            title: t('about.features.european'),
            description: t('about.features.europeanDesc'),
        },
    ];

    const benefits = [
        t('about.benefits.noLimit'),
        t('about.benefits.competitive'),
        t('about.benefits.fast'),
        t('about.benefits.expert'),
        t('about.benefits.transparent'),
        t('about.benefits.multilingual'),
    ];

    return (
        <section id="about" className="py-20 lg:py-28 bg-background relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-primary/5 to-transparent" />
            <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <Badge 
                            variant="outline" 
                            className="mb-6 border-primary/30 text-primary bg-primary/5 px-4 py-1.5 rounded-full"
                        >
                            <Building2 className="w-3 h-3 mr-2" />
                            {t('about.badge')}
                        </Badge>

                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
                            {t('about.title1')}{' '}
                            <span className="text-gradient-emerald">{t('about.title2')}</span>{' '}
                            {t('about.title3')}
                        </h2>

                        <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                            {t('about.description')}
                        </p>

                        <div className="grid sm:grid-cols-2 gap-3 mb-10">
                            {benefits.map((benefit, index) => (
                                <div key={index} className="flex items-center gap-3">
                                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                                    <span className="text-foreground">{benefit}</span>
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <a href="#simulator">
                                <Button 
                                    size="lg"
                                    className="btn-premium text-primary-foreground px-8 rounded-full group"
                                >
                                    <span className="relative z-10 flex items-center">
                                        {t('about.startNow')}
                                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </Button>
                            </a>
                            <Button 
                                variant="outline" 
                                size="lg"
                                className="border-border hover:border-primary px-8 rounded-full"
                                onClick={onContactClick}
                            >
                                {t('about.contactUs')}
                            </Button>
                        </div>
                    </div>

                    <div className="relative">
                        <div className="relative mb-8 lg:mb-0">
                            <div className="rounded-3xl overflow-hidden shadow-elegant">
                                <img
                                    src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&auto=format&fit=crop&q=80"
                                    alt="Professional Financial Consultant"
                                    className="w-full h-80 object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-foreground/20 to-transparent" />
                            </div>
                            <div className="absolute -z-10 -bottom-4 -right-4 w-full h-full rounded-3xl border-2 border-accent/30" />
                        </div>

                        <div className="grid grid-cols-2 gap-4 lg:absolute lg:-bottom-20 lg:left-0 lg:right-0">
                            {features.map((feature, index) => {
                                const Icon = feature.icon;
                                return (
                                    <Card key={index} className="card-premium p-5 bg-card">
                                        <div className="icon-emerald w-12 h-12 mb-4">
                                            <Icon className="w-6 h-6" />
                                        </div>
                                        <h4 className="font-semibold text-foreground mb-2 text-sm">
                                            {feature.title}
                                        </h4>
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            {feature.description}
                                        </p>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
